/**
 * Pure JWT payload/expiry helpers. No side effects. No storage access.
 *
 * Fail-closed semantics: every error path returns the "worst" value
 * (null for payload/expiry, true for expired) so a malformed token is
 * never treated as valid.
 *
 * SECURITY: these helpers DECODE only — they perform NO signature
 * verification. A decoded payload is display/UX data, never an
 * authorization input. The API is the sole trust boundary (HARD-JWTC-001).
 *
 * @module @puffin-core/auth-token-storage/jwtExpiry
 */

/**
 * Decode a JWT's payload segment and return it as a plain object.
 *
 * Returns null for any of: non-string input, empty string, tokens without
 * exactly three dot-separated segments, bad base64url payload, payload that
 * is not JSON, or a payload that is not a plain object (arrays, primitives,
 * and literal null all fail closed).
 *
 * @param {string} token - A JWT
 * @returns {Object|null} The decoded claims object, or null if undecodable
 */
export function decodeJwtPayload(token) {
  if (typeof token !== 'string' || token.length === 0) {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    const payload = JSON.parse(decoded);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }
    return payload;
  } catch (error) {
    // The token is untrusted input, so its two decode failures are EXPECTED and fail closed:
    // `atob` refuses a non-base64 segment (`InvalidCharacterError`), `JSON.parse` a non-JSON one
    // (`SyntaxError`). Anything else is a defect and surfaces (SWALLOWED-CATCH-BURNDOWN-001 U3).
    if (error instanceof SyntaxError || error?.name === 'InvalidCharacterError') return null;
    throw error;
  }
}

/**
 * Decode a JWT's `exp` claim and return it as milliseconds since epoch.
 *
 * Returns null for any token `decodeJwtPayload` rejects, plus payloads
 * with a missing or non-numeric `exp` claim.
 *
 * @param {string} token - A JWT access token
 * @returns {number|null} `exp * 1000` or null if undecodable
 */
export function decodeJwtExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp * 1000;
}

/**
 * Check whether a JWT is expired relative to a supplied "now" (defaults to Date.now()).
 *
 * Fail-closed: returns true for any token that cannot be decoded, so an
 * unparseable token is never treated as valid.
 *
 * @param {string} token - A JWT access token
 * @param {number} [now=Date.now()] - Reference time in ms (injectable for tests)
 * @returns {boolean} true if the token is missing, malformed, or past its `exp`
 */
export function isJwtExpired(token, now = Date.now()) {
  const expMs = decodeJwtExpiryMs(token);
  if (expMs === null) {
    return true;
  }
  return expMs <= now;
}
